import { useEffect } from "react";
import { useDroppable, useDndMonitor } from "@dnd-kit/core";
import { MOBILE_LIBRARY_DRAG_CANCEL_DROP_ID } from "../constants/libraryDragDndIds";
import { useIsMobile } from "../hooks/useIsMobile";
import { useMobileEdgeChromeStore } from "../stores/mobileEdgeChromeStore";

/**
 * Narrow viewport only: hides app chrome via store while dragging library/terrain presets,
 * and shows a cancel drop target pill at the bottom of the screen.
 */
export default function MobileLibraryDragChrome() {
  const isMobile = useIsMobile();
  const immersive = useMobileEdgeChromeStore((s) => s.immersiveLibraryDragActive);
  const setImmersive = useMobileEdgeChromeStore((s) => s.actions.setImmersiveLibraryDragActive);

  useDndMonitor({
    onDragStart: (event) => {
      const d = event?.active?.data?.current;
      if (!isMobile || !d) return;
      if (d.dragSource === "objectLibrary" || d.dragSource === "terrainPanel") {
        setImmersive(true);
      }
    },
    onDragEnd: () => setImmersive(false),
    onDragCancel: () => setImmersive(false),
  });

  const { setNodeRef, isOver } = useDroppable({
    id: MOBILE_LIBRARY_DRAG_CANCEL_DROP_ID,
    disabled: !isMobile || !immersive,
  });

  useEffect(() => {
    if (!immersive) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && typeof active.blur === "function") {
      active.blur();
    }
  }, [immersive]);

  if (!isMobile || !immersive) return null;

  return (
    <div
      ref={setNodeRef}
      className="pointer-events-auto fixed left-1/2 z-[10050] -translate-x-1/2 touch-none select-none rounded-full border border-white/45 bg-gray-950/95 px-5 py-2.5 text-sm font-semibold text-gray-100 shadow-lg"
      style={{ bottom: "max(16px, env(safe-area-inset-bottom, 0px))" }}
    >
      {isOver ? "Release to cancel" : "Drag here to cancel"}
    </div>
  );
}
